import { defineComponent, reactive, SetupContext, ref, watch } from '@vue/composition-api';
import SystemConst from '@/shared/system-const';
import HttpStatus from 'http-status-codes';
import { Category } from '@/shared/type';
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';          // 共通メッセージダイアログコンポーネント
import vueRouter from '@/router';
import MessageConst from '@/shared/message-const';

/**
 * リアクティブな変数の型定義
 */
export type reactiveData = {
    windowSize: { x: number; y: number };                                      // Windowサイズ
    categoryItem: Category[];                                                  // カテゴリ
};

/**
 * （教育用コンテンツ）カテゴリコンポーネント
 */
export default defineComponent({
    components: { CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                        // Windowサイズ
            categoryItem: []                                                   // カテゴリ
        });
        const selectCategoryHeight = SystemConst.SELECT_CATEGORY_SIZE.HEIGHT;  // カテゴリー選択高さ調整用変数
        const commonRef = ref<any>(null);                                      // 共通ダイアログ
        const activeNode = ref<any[]>([]);                                     // カテゴリツリーでactiveなノードを管理する変数
        const router = vueRouter;                                              // 画面遷移用

        /**
         * windowサイズ設定（本関数が使用されるのは親コンポーネントのためvueで使用しないが、エラーになるためreturnに記述）
         * @param windowSize Windowサイズ
         */
        function resize(windowSize: any) {
            state.windowSize = windowSize;
        }

        /**
         * 初期表示
         */
        created();
        function created() {
            // ストアに保存されているカテゴリ情報とアクティブ状態のノードを復元する
            state.categoryItem = ctx.root.$store.state.category.contentCategory;
            activeNode.value[0] = ctx.root.$store.state.content.activeNode[0];
        }

        /** カテゴリの取得 */
        async function getCategory() {
            // 教育用コンテンツカテゴリ情報取得APIを呼び出す
            // GET /api/content-categories
            // パラメータ：なし
            const response = await ctx.root.$store.dispatch('category/getContentCategory');

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 取得したデータをstate.categoryItemに設定する
            state.categoryItem = response.data.categories;

            // カテゴリ情報をストアに保存する
            ctx.root.$store.commit('category/setContentCategory', state.categoryItem);

            if (ctx.root.$store.state.content.activeNode.length === 0) {
                // ストアにアクティブなノードが保存されていない場合
                // 取得したカテゴリの1番目のカテゴリをアクティブにする
                activeNode.value = [state.categoryItem[0]['id']];
            } else {
                // ストアにアクティブなノードが保存されている場合
                if (state.categoryItem.map(item => item['id']).includes(ctx.root.$store.state.content.activeNode[0])) {
                    // ストアに保存しているノードが取得したカテゴリ情報と一致する場合
                    // ストアに保存しているノードをアクティブにする
                    activeNode.value = ctx.root.$store.state.content.activeNode;
                } else {
                    // ストアに保存しているノードが取得したカテゴリ情報と一致しない場合
                    // エラー画面へ遷移
                    ctx.root.$store.commit('setErrorMessage', MessageConst.E_INVALID_CATEGORY);
                    router.push('/error');
                }
            }
        }

        /**
         * カテゴリツリーでノードのactive情報が更新された際に発火する
         * 
         * @param node nodeオブジェクト
         */
        watch(activeNode, () => watchActiveNode());
        function watchActiveNode() {
            // 選択されたノードを再度押下し、カテゴリ選択が未選択となった場合
            if (activeNode.value.length === 0) {
                // 直前にアクティブだったノードを再びアクティブにする
                activeNode.value[0] = ctx.root.$store.state.content.activeNode[0];
            } else {
                // 前回選択されたノードと別のノードを選択された場合
                if (activeNode.value[0] !== ctx.root.$store.state.content.activeNode[0]) {
                    // ストアにactiveなノードの状態を保存する
                    ctx.root.$store.commit('content/setActiveNode', activeNode.value);
                }
            }
        }

        /**
         * 親コンポーネントでアクティブ情報を更新した場合の処理
         */
        function onActive() {
            // 親コンポーネントで保存されたアクティブ状態のノードを復元する
            ctx.root.$set(activeNode.value, 0, ctx.root.$store.state.content.activeNode[0]);
        }

        return {
            state,
            selectCategoryHeight,
            commonRef,
            activeNode,
            resize,
            getCategory,
            onActive
        };
    }
});
